import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Search, Menu, X, Sun, Moon, ChevronDown, Layers, Clock, MapPin } from "lucide-react";
import { Logo } from "./Logo";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "@/components/NotificationsBell";
import { useTheme } from "@/hooks/useTheme";
import { useCategories } from "@/hooks/useCategories";
import { useSearchResults } from "@/hooks/useSearchResults";
import { useUserRegion } from "@/hooks/useUserRegion";
import { getCategoryTheme } from "@/lib/categoryTheme";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { DynamicIcon } from "@/components/ui/DynamicIcon";
import { LanguageSelector } from "@/components/ui/LanguageSelector";

/** Fixed primary navigation items — categories no longer clutter the navbar. */
const PRIMARY_NAV = [
  { label: "Home", href: "/" },
  { label: "Categories", href: "/categories" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
] as const;

export function Header() {
  const { theme, toggleTheme } = useTheme();
  const { categories } = useCategories();
  const { regionInfo, currentTime, currentDate } = useUserRegion();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [themeIconSpin, setThemeIconSpin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Header's search dropdown is a quick-peek only — top 5 matches inline.
  // Anything beyond that takes the visitor to the full /search page, which
  // has the proper category + date filters, pagination and ArticleCard grid.
  const { results, total, loading } = useSearchResults({
    q: debouncedQuery,
    cat: "all",
    sort: "newest",
    page: 1,
    pageSize: 5,
  });

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click-outside + Escape + Cmd/Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchFocused(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        setSearchFocused(false);
        setSearchQuery("");
        searchInputRef.current?.blur();
        return;
      }
      // Cmd+K (Mac) or Ctrl+K (Win/Linux) — focus search
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        setSearchFocused(true);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const isActive = (href: string) =>
    href === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(href);

  const handleResultClick = (slug: string) => {
    setSearchFocused(false);
    setSearchQuery("");
    navigate(`/article/${slug}`);
  };

  const goToSearchPage = () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearchFocused(false);
    setSearchQuery("");
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    goToSearchPage();
  };

  const handleToggleTheme = useCallback(() => {
    setThemeIconSpin(true);
    toggleTheme();
    const t = setTimeout(() => setThemeIconSpin(false), 600);
    return () => clearTimeout(t);
  }, [toggleTheme]);

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
      <div className="container flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-8 flex-1">
          <Logo />

          {/* Desktop navigation — fixed links only */}
          <nav className="hidden lg:flex items-center gap-1">
            {PRIMARY_NAV.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-active={isActive(link.href)}
                className={cn(
                  "nav-link px-3 py-1 text-sm font-bold uppercase tracking-wider rounded-md transition-colors duration-200",
                  isActive(link.href)
                    ? "text-foreground"
                    : "text-foreground/65 hover:text-foreground",
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {/* Integrated Search Box with working Cmd+K shortcut */}
          <div ref={searchRef} className="relative hidden md:block group">
            <form onSubmit={submitSearch} className="relative z-10">
              <Search
                className={cn(
                  "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors",
                  searchFocused ? "text-primary" : "text-muted-foreground",
                )}
              />
              <motion.input
                id="header-search-input"
                ref={searchInputRef}
                initial={false}
                animate={{ width: searchFocused ? 320 : 200 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                type="search"
                role="searchbox"
                aria-label="Search articles"
                autoComplete="off"
                value={searchQuery}
                onFocus={() => setSearchFocused(true)}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="h-10 pl-10 pr-16 rounded-full bg-accent/50 border-transparent border focus:border-primary/30 focus:bg-background text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              {/* Cmd+K hint — functional shortcut registered above */}
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden sm:flex items-center gap-0.5 text-[10px] text-muted-foreground/50 font-medium bg-accent/70 px-1.5 py-0.5 rounded">
                <span>
                  {/Mac|iPhone|iPad/i.test(navigator.userAgent) ? "⌘" : "Ctrl"}
                </span>
                <span>K</span>
              </div>
            </form>

            <AnimatePresence>
              {searchFocused && (searchQuery.trim() || results.length > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 5, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  data-testid="search-overlay"
                  className="absolute top-full right-0 bg-background border rounded-2xl shadow-2xl overflow-hidden mt-2 z-50 p-3 w-[calc(100vw-2rem)] sm:w-[380px] md:w-[400px] max-w-[90vw]"
                >
                  {loading && !results.length ? (
                    <div className="p-8 text-center">
                      <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                      <p className="text-xs text-muted-foreground">
                        Searching articles…
                      </p>
                    </div>
                  ) : results.length === 0 ? (
                    <div className="p-8 text-center">
                      <p className="text-sm text-muted-foreground">
                        No results for "{searchQuery}"
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="px-3 py-2 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                        Top {results.length} of {total} results
                      </div>
                      {results.map((article) => (
                        <button
                          key={article.id}
                          onClick={() => handleResultClick(article.slug)}
                          className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-accent transition-all group text-left"
                        >
                          <div className="h-12 w-12 rounded-lg bg-muted overflow-hidden flex-shrink-0">
                            <img
                              src={article.image}
                              alt=""
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).src =
                                  "/og-image.svg";
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                              {article.title}
                            </h4>
                            <span
                              style={{
                                color: getCategoryTheme(article.category || article.categoryLabel).color,
                              }}
                              className="text-[11px] font-bold uppercase tracking-wider"
                            >
                              {article.categoryLabel}
                            </span>
                          </div>
                        </button>
                      ))}

                      {total > results.length && (
                        <button
                          onClick={goToSearchPage}
                          className="w-full mt-1 p-3 text-xs font-bold text-primary hover:bg-primary/5 transition-colors text-center border-t border-border flex items-center justify-center gap-2"
                        >
                          See all {total} results{" "}
                          <ChevronDown className="h-3 w-3 -rotate-90" />
                        </button>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <NotificationsBell />
            <div className="h-6 w-px bg-border mx-1 hidden sm:block" />
            {/* User Region & Live Time Pill */}
            <div
              className="hidden xl:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/40 border border-border/60 text-xs font-semibold text-foreground/80 hover:bg-accent/70 transition-colors"
              title={`${regionInfo.regionName} • ${currentDate}`}
            >
              <Clock className="h-3.5 w-3.5 text-primary" />
              <span>{currentTime}</span>
              <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">
                {regionInfo.timeZoneAbbr}
              </span>
            </div>

            {/* Language Selector */}
            <LanguageSelector variant="header" />

            {/* Account Menu / Sign In button & Avatar */}
            <AccountMenu variant="desktop" />

            {/* Theme toggle with spin animation */}
            <button
              onClick={handleToggleTheme}
              className="h-10 w-10 flex items-center justify-center rounded-full text-foreground/70 hover:bg-accent transition-colors"
              aria-label="Toggle theme"
            >
              <span
                className={
                  themeIconSpin ? "theme-icon-spin inline-flex" : "inline-flex"
                }
                aria-hidden="true"
              >
                {theme === "light" ? (
                  <Moon className="h-[18px] w-[18px]" />
                ) : (
                  <Sun className="h-[18px] w-[18px]" />
                )}
              </span>
            </button>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden h-10 w-10 flex items-center justify-center rounded-full text-foreground/70 hover:bg-accent transition-colors"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden border-t bg-background overflow-y-auto max-h-[calc(100dvh-4rem)] scrollbar-hide"
          >
            <div className="container py-6 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="mobile-search-input"
                  type="search"
                  role="searchbox"
                  aria-label="Search articles"
                  autoComplete="off"
                  placeholder="Search articles…"
                  className="w-full h-11 pl-10 pr-4 rounded-xl bg-accent border-transparent focus:bg-background transition-all text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      navigate(
                        `/search?q=${encodeURIComponent((e.target as HTMLInputElement).value)}`,
                      );
                      setMobileMenuOpen(false);
                    }
                  }}
                />
              </div>

              <nav className="flex flex-col gap-1">
                {PRIMARY_NAV.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "px-4 py-3 rounded-xl text-sm font-bold uppercase tracking-wider transition-colors flex items-center gap-3",
                      isActive(link.href)
                        ? "bg-primary/10 text-primary"
                        : "hover:bg-accent text-foreground/70",
                    )}
                  >
                    {link.href === "/categories" && (
                      <Layers className="h-4 w-4" />
                    )}
                    {link.label}
                  </Link>
                ))}

                {/* Categories quick-links in mobile */}
                <div className="mt-3 pt-3 border-t">
                  <p className="px-4 text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
                    Browse Categories
                  </p>
                  <div className="flex overflow-x-auto scrollbar-hide gap-2 px-4 py-1 pb-2 touch-pan-x">
                    {categories.map((c) => (
                      <Link
                        key={c.id}
                        to={`/category/${c.slug}`}
                        onClick={() => setMobileMenuOpen(false)}
                        className="px-3 py-1.5 flex items-center gap-1.5 rounded-full text-xs font-medium border border-border hover:border-primary/40 hover:bg-primary/5 transition-colors whitespace-nowrap shrink-0"
                        style={{ color: getCategoryTheme(c.slug || c.name).color }}
                      >
                        <DynamicIcon name={c.slug || c.icon || "Hash"} className="w-3.5 h-3.5" />
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </nav>

              <div className="pt-3 border-t space-y-3">
                {/* Local Region & Time display in mobile menu */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-accent/40 border border-border/60 text-xs">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">{regionInfo.regionName}</p>
                      <p className="text-[10px] text-muted-foreground">{currentDate}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-foreground font-mono">{currentTime}</span>
                  </div>
                </div>

                <LanguageSelector variant="mobile" />
                <AccountMenu variant="mobile" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
